import { Component, OnInit } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { forkJoin } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { AlertService } from "src/app/shared-module/services";
import { ImportReportService } from "../../services/import-report.service";
import { IMPORT_REPORT_CATEGORY_LABELS, ImportReportCategories, ImportReportRecipient } from "../../models/import-report";

// editable copy of a recipient; categories are tracked as checkbox state
interface RecipientDraft {
  email: string;
  categories: { [category: string]: boolean };
}

@Component({
  selector: "app-import-report",
  templateUrl: "./import-report.component.html",
  styleUrls: ["./import-report.component.scss"]
})
export class ImportReportComponent implements OnInit {

  loading = false;
  saving = false;
  categories: string[] = [];
  categorySources: ImportReportCategories = {};
  recipients: ImportReportRecipient[] = [];

  newRecipient: RecipientDraft = this.emptyDraft();

  editingId: number | null = null;
  editDraft: RecipientDraft = this.emptyDraft();

  confirmingDeleteId: number | null = null;

  constructor(
    private importReportService: ImportReportService,
    private alertService: AlertService,
    private toastrService: ToastrService,
  ) { }

  ngOnInit(): void {
    this.loading = true;
    forkJoin({
      categories: this.importReportService.getCategories(),
      recipients: this.importReportService.getRecipients(),
    }).subscribe({
      next: ({ categories, recipients }) => {
        this.categorySources = categories || {};
        this.categories = Object.keys(this.categorySources).sort();
        this.recipients = recipients || [];
        this.newRecipient = this.emptyDraft();
        this.loading = false;
      },
      error: error => {
        this.alertService.error(this.errorMessage(error));
        this.loading = false;
      }
    });
  }

  categoryLabel(category: string): string {
    return IMPORT_REPORT_CATEGORY_LABELS[category] || category;
  }

  categorySourcesTitle(category: string): string {
    return (this.categorySources[category] || []).join(", ");
  }

  // ADD

  addRecipient() {
    const recipient = this.toRecipient(this.newRecipient);
    const invalid = this.validate(recipient);
    if (invalid) {
      this.alertService.error(invalid);
      return;
    }

    this.saving = true;
    this.importReportService.addRecipient(recipient).subscribe({
      next: saved => {
        // adding an existing email updates it on the backend, so replace rather than duplicate
        this.recipients = [...this.recipients.filter(r => r.id !== saved.id), saved]
          .sort((a, b) => a.email.localeCompare(b.email));
        this.newRecipient = this.emptyDraft();
        this.saving = false;
        this.toastrService.success(`Added ${saved.email}`);
      },
      error: error => {
        this.alertService.error(this.errorMessage(error));
        this.saving = false;
      }
    });
  }

  // EDIT

  startEdit(recipient: ImportReportRecipient) {
    this.confirmingDeleteId = null;
    this.editingId = recipient.id;
    this.editDraft = this.toDraft(recipient);
  }

  cancelEdit() {
    this.editingId = null;
  }

  saveEdit(recipient: ImportReportRecipient) {
    const updated = this.toRecipient(this.editDraft);
    const invalid = this.validate(updated);
    if (invalid) {
      this.alertService.error(invalid);
      return;
    }

    this.saving = true;
    this.importReportService.updateRecipient(recipient.id, updated).subscribe({
      next: saved => {
        this.recipients = this.recipients
          .map(r => r.id === saved.id ? saved : r)
          .sort((a, b) => a.email.localeCompare(b.email));
        this.editingId = null;
        this.saving = false;
        this.toastrService.success(`Updated ${saved.email}`);
      },
      error: error => {
        this.alertService.error(this.errorMessage(error));
        this.saving = false;
      }
    });
  }

  // DELETE

  confirmDelete(recipient: ImportReportRecipient) {
    this.editingId = null;
    this.confirmingDeleteId = recipient.id;
  }

  cancelDelete() {
    this.confirmingDeleteId = null;
  }

  deleteRecipient(recipient: ImportReportRecipient) {
    this.saving = true;
    this.importReportService.deleteRecipient(recipient.id).subscribe({
      next: () => {
        this.recipients = this.recipients.filter(r => r.id !== recipient.id);
        this.confirmingDeleteId = null;
        this.saving = false;
        this.toastrService.info(`Removed ${recipient.email}`);
      },
      error: error => {
        this.alertService.error(this.errorMessage(error));
        this.saving = false;
      }
    });
  }

  // HELPERS

  private emptyDraft(): RecipientDraft {
    const categories = {};
    this.categories?.forEach(category => categories[category] = false);
    return { email: "", categories };
  }

  private toDraft(recipient: ImportReportRecipient): RecipientDraft {
    const draft = this.emptyDraft();
    draft.email = recipient.email;
    (recipient.categories || []).forEach(category => draft.categories[category] = true);
    return draft;
  }

  private toRecipient(draft: RecipientDraft): ImportReportRecipient {
    return {
      email: (draft.email || "").trim(),
      categories: this.categories.filter(category => draft.categories[category]),
    };
  }

  // mirrors the backend checks so obvious mistakes don't need a round trip
  private validate(recipient: ImportReportRecipient): string | null {
    if (!recipient.email || !recipient.email.includes("@")) {
      return "Enter a valid email address.";
    }
    if (!recipient.categories.length) {
      return "Select at least one category.";
    }
    return null;
  }

  private errorMessage(error: any): string {
    if (error instanceof HttpErrorResponse && error.error?.error) {
      return error.error.error;
    }
    return error?.message || "Something went wrong.";
  }
}
